'use client';

import React from 'react';
import { SignIn } from '@clerk/nextjs';
import { clerkAppearance } from '@/app/auth/clerkAppearance';

type SignInContentProps = {
    redirectUrl: string;
    useHashRouting?: boolean;
};

/**
 * Renders Clerk's SignIn in the auth modal.
 * Passes oauthRedirectStrategy="popup" so OAuth (e.g. Google) opens in a popup
 * instead of redirecting the main window to Clerk's domain (avoids the URL flash).
 * The prop may not exist in older @clerk/nextjs types; it is passed via spread so
 * runtimes that support it will use it.
 */
export default function SignInContent({ redirectUrl, useHashRouting = false }: SignInContentProps) {
    // Thread the destination into the cross-link so the in-form "Sign up" ⇄ "Sign in" toggle preserves it.
    // Clerk navigates to signUpUrl/signInUrl verbatim; a static path drops redirect_url and lands the user
    // on "/" after auth (the storefront Sign Up → Sign In bug).
    const redirectQuery = `?redirect_url=${encodeURIComponent(redirectUrl)}`;

    const signInProps = {
        appearance: clerkAppearance,
        signUpUrl: `/auth/signup${redirectQuery}`,
        signInUrl: `/auth/signin${redirectQuery}`,
        forceRedirectUrl: redirectUrl,
        fallbackRedirectUrl: '/',
        signUpForceRedirectUrl: redirectUrl,
        signUpFallbackRedirectUrl: '/',
        ...(useHashRouting ? { routing: 'hash' as const } : {}),
        // Use popup for OAuth so the main window doesn't redirect to Clerk's domain
        oauthRedirectStrategy: 'popup',
    } as React.ComponentProps<typeof SignIn>;

    return <SignIn {...signInProps} />;
}
